'use client';
import React from 'react';
import { X, CheckCheck, Bell, AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';
import { useGlobalStore } from '@/store/useGlobalStore';
import { motion } from 'framer-motion';
import api from '@/utils/api';
import { useTranslation } from 'react-i18next';

interface Props {
  onClose: () => void;
}

const iconMap = {
  success: <CheckCircle size={16} className="text-positive" />,
  warning: <AlertTriangle size={16} className="text-warning" />,
  error: <XCircle size={16} className="text-negative" />,
  info: <Info size={16} className="text-info" />,
};

export default function NotificationPanel({ onClose }: Props) {
  const { notifications, markNotificationsRead } = useGlobalStore();
  const { t } = useTranslation();

  const handleNotificationClick = async (notif: typeof notifications[0]) => {
    if (!notif.read) {
      useGlobalStore.setState((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === notif.id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    }

    try {
      await api.post('/application/profile/notifications/read', { uuid: notif.id });
    } catch {
      // silent fail
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        className="mx-4 mt-16 rounded-2xl overflow-hidden"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2">
            <Bell size={16} className="text-primary" />
            <span className="font-semibold text-sm text-foreground">{t('notificationsTitle')}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={markNotificationsRead}
              className="text-xs text-primary flex items-center gap-1 px-2 py-1 rounded-lg transition-all active:scale-95"
              style={{ background: 'var(--warning-bg)' }}
            >
              <CheckCheck size={12} />
              {t('markAllRead')}
            </button>
            <button onClick={onClose} className="p-1.5 rounded-xl transition-all active:scale-95" style={{ background: 'var(--muted)' }}>
              <X size={14} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto scrollbar-hide">
          {notifications.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">{t('noNotifications')}</div>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className="w-full flex items-start gap-3 px-4 py-3 transition-colors text-left"
                style={{
                  borderBottom: '1px solid var(--border)',
                  background: n.read ? 'transparent' : 'rgba(245,158,11,0.05)',
                }}
              >
                <div className="mt-0.5 flex-shrink-0">{iconMap[n.type]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm font-semibold ${n.read ? 'text-muted-foreground' : 'text-foreground'}`}>
                      {n.title}
                    </p>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {n.read ? (
                        <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
                          {t('read')}
                        </span>
                      ) : (
                        <>
                          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(245,158,11,0.15)', color: 'var(--primary)' }}>
                            {t('unread')}
                          </span>
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {new Date(n.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}